import { useEffect, useState, type FC } from "react";
import { generateUUID } from "@services/api/authentication/generate-uuid/generateUUID";
import type { OfferAssesmentFormProps } from "./index";
import OfferAssesmentForm from "./index";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TechnicalAssessmentList from "@components/ui/offer-technical-assessment/assessment-list";
import { useLanguage } from "../../../../contexts/LanguageContext";
import translations from "./Modal.translations.json";
import { updateOfferAssessment } from "@services/api/offer-assesment/update/update";
import { offerAssesmentValue } from "@components/ui/offer-assesment/types/offerAssesmentValue";
import { getOfferAssesmentByOffer } from "@services/api/offer-assesment/getByOffer/getByOffer";
import { buildOfferAssesmentProps } from "@services/offer-assesment/buildOfferAssesmentProps";

type SupportedLanguage = keyof typeof translations;

interface OfferAssesmentModalFormProps extends OfferAssesmentFormProps {
  open: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  offer: any;
}

const DARK_BG = "#121824";
const DARK_BORDER = "#1e2638";
const TAB_ACTIVE = "#4f8ef7";

const OfferAssesmentModalForm: FC<OfferAssesmentModalFormProps> = ({
  formBuilderAccessToken,
  successMessage,
  open,
  offer,
  onClose,
  onRefetch
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  
  const [defaultAssesmentValue, setDefaultAssesmentValue] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchOfferAssesmentByOffer = async () => {
    setLoading(true);
    const offerAssesment = await getOfferAssesmentByOffer(offer.uuid);

    if (offerAssesment?.data?.items?.length > 0) {
      const props = buildOfferAssesmentProps({
        uuid: offerAssesment?.data?.uuid,
        offer: offerAssesment?.data?.offer,
        items: offerAssesment?.data?.items || [],
      });
      setDefaultAssesmentValue(props);
    } else {
      const uuid = await generateUUID();
      const props = buildOfferAssesmentProps({
        uuid,
        offer: offerAssesment?.data?.offer,
        items: [],
      });
      setDefaultAssesmentValue(props);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchOfferAssesmentByOffer();
    }
  }, [open])

  const handleSubmit = async (data: any) => {
    const questions = data?.[offerAssesmentValue.group]?.[offerAssesmentValue.subGroup.information]?.[offerAssesmentValue.field.items]
    setLoading(true);
    try {
      await updateOfferAssessment(offer?.id, questions);
      fetchOfferAssesmentByOffer();
    } finally {
      setLoading(false);
      onRefetch && onRefetch();
    }
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: DARK_BG,
          color: "#ffffff"
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          justify: "space-between", 
          alignItems: "center",
          borderBottom: `1px solid ${DARK_BORDER}`,
          px: 3,
          py: 2,
          fontWeight: 700,
          color: "#ffffff"
        }}
      >
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {content.title} ({offer?.title}-{offer?.location})
        </Typography>

        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            color: "#8c94a4",
            "&:hover": {
              color: "#ffffff",
              backgroundColor: "rgba(255, 255, 255, 0.08)"
            }
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: `1px solid ${DARK_BORDER}`, px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            "& .MuiTab-root": { color: "#8c94a4", textTransform: "none", fontWeight: 600 },
            "& .Mui-selected": { color: TAB_ACTIVE },
            "& .MuiTabs-indicator": { backgroundColor: TAB_ACTIVE }
          }}
        >
          <Tab label={content.general} />
          <Tab label={content.technical} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3, backgroundColor: DARK_BG }}>
        <Box sx={{ display: activeTab === 0 ? "flex" : "none", flexDirection: "column", gap: 2, pt: 1 }}>
          <OfferAssesmentForm 
            onSubmit={handleSubmit}
            defaultValue={defaultAssesmentValue}
            formBuilderAccessToken={formBuilderAccessToken}
            successMessage={successMessage}
            loading={loading}
          />
        </Box>
        <Box sx={{ display: activeTab === 1 ? "flex" : "none", flexDirection: "column", gap: 2, pt: 1 }}>
          <TechnicalAssessmentList
            offerId={offer?.id}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OfferAssesmentModalForm;